/* TRENDS — 90 days of weight, and where the tape has moved.

   Read-only, and deliberately arithmetic-free: every number on this screen that
   is a *derived* number — the rolling 7-day average, the 7- and 30-day deltas,
   the per-metric tape deltas — is computed by `src/milo/queries.py` and served
   by `GET /trends`. The same three sections back the MCP `get_trends` tool, so
   recomputing any of them here would let the app and the coach quote different
   figures for the same week. Nothing below re-derives them.

   What this screen *does* compute is geometry: pixel coordinates for the chart
   and the sparklines. Those are a property of the drawing, not of the data.

   Two reads, not one, because they fail differently:

   - `/trends` (+ `/goals`) is the screen. Without it there is nothing to draw.
   - `/history` × 5 supplies only the *shape* of each tape sparkline — /trends
     gives the endpoints and the deltas but no series. If it fails, the TAPE.90D
     rows still render with their real values and chips, minus the sparkline.

   There is no charting library here on purpose: the design specifies exact
   stroke widths, dash patterns and label positions, and the whole plot is
   forty lines of SVG. */
import { useEffect, useState } from 'react'
import { Alert, Metric, Window } from '../ds'
import {
  errorMessage,
  getGoals,
  getHistory,
  getTrends,
  type HistoryPoint,
  type Metric as MetricName,
  type TrendsResponse,
  type WeightTrendPoint,
} from '../api'
import { useShellSync } from '../app/syncOnLoad'
import '../styles/trends.css'

/* ── Constants ───────────────────────────────────────────────────────────── */

/** The window the screen is named for. Also the `days` sent to /trends. */
const TREND_DAYS = 90

/** Tape metrics, in the order the design lists them. Weight is not one: it has
 *  its own richer section, and `/trends` never keys it under `measurements`. */
const TAPE_METRICS: readonly { metric: Exclude<MetricName, 'weight'>; label: string }[] = [
  { metric: 'waist', label: 'WAIST' },
  { metric: 'hips', label: 'HIPS' },
  { metric: 'chest', label: 'CHEST' },
  { metric: 'arms', label: 'ARMS' },
  { metric: 'thighs', label: 'THIGHS' },
]

/** `GET /goals` is a flat map of strings. These are the keys Milo writes. */
const GOAL_WEIGHT_KEY = 'target_weight'
const GOAL_RATE_KEY = 'weekly_rate'

/* Chart geometry, straight from the prototype: a 720×300 viewBox scaled to the
   window body by CSS, with the plot inset far enough on the left for a 3-digit
   mono gridline label and far enough at the bottom for nothing at all — the
   x-axis carries no ticks, only the 90-day span the window title states. */
const W = 720
const H = 300
const PAD_L = 44
const PAD_R = 14
const PAD_T = 18
const PAD_B = 26

/** Gridline spacing, in pounds. */
const GRID_STEP = 5

/** Ceiling on gridline count. 90 days of honest weight never approaches it;
 *  one absurd row (a kg reading typed into a lb field, say) otherwise turns
 *  into thousands of `<line>`s and freezes the tab. */
const MAX_GRIDLINES = 40

/** Headroom above the highest reading and below the lowest, in pounds. Also the
 *  reason the y-domain can never be zero-height, so no scale ever divides by 0. */
const Y_PAD = 1.5

/* ── Loading ─────────────────────────────────────────────────────────────── */

type Load<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error'; message: string }

interface Core {
  trends: TrendsResponse
  /** The pink line and the ROLLING 7D chip. Null when no goal has been set. */
  goalWeight: number | null
  /** The RATE / WK chip. Null when no goal has been set. */
  weeklyRate: number | null
}

/** Metric name → its 90-day series, for the sparklines only. */
type TapeSeries = Partial<Record<string, number[]>>

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

/* ── Formatting ──────────────────────────────────────────────────────────── */

/**
 * A signed delta: `−1.1`, `+0.4`, `—` when there isn't one.
 *
 * The sign is U+2212 MINUS, not a hyphen, matching every other number in the
 * design. Exact zero is rendered unsigned: the prototype's `v > 0 ? '+' : '−'`
 * would print `−0.0`, which reads as a loss that did not happen. `delta_30d`
 * comes back as exactly 0.0 for a metric that has not moved, so this is a real
 * case rather than a hypothetical one.
 */
function delta(value: number | null): string {
  if (value === null) return '—'
  if (value === 0) return '0.0'
  return (value > 0 ? '+' : '−') + Math.abs(value).toFixed(1)
}

/** A weight, to the one decimal place every number on this screen carries. */
function lb(value: number | null): string {
  return value === null ? '—' : value.toFixed(1)
}

/* ── Dates ───────────────────────────────────────────────────────────────── */

/**
 * `YYYY-MM-DD` → local midnight on that calendar day.
 *
 * Built from the parts rather than `new Date(iso)`, which reads a bare date as
 * UTC midnight and so lands on the previous day everywhere west of Greenwich —
 * which is everywhere Milo runs. Same reasoning as `format.ts`'s `monoDate`.
 */
function parseIso(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/** Whole days from `a` to `b`. Rounded, so a DST boundary inside the span — an
 *  hour short or an hour long — does not shift a point by a day. */
function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86_400_000)
}

/* ── The chart ───────────────────────────────────────────────────────────── */

interface Gridline {
  y: number
  /** Baseline for the label, nudged down so it centres on the line. */
  labelY: number
  label: string
}

interface Chart {
  gridlines: Gridline[]
  /** One dot per day that was actually weighed. */
  dots: { x: number; y: number }[]
  /** The rolling 7-day average — the headline series. */
  rollingPath: string
  /** Null when no goal weight is set: no line, and no label. */
  goal: { y: number; labelY: number; label: string } | null
}

function round1(value: number): number {
  return Math.round(value * 10) / 10
}

/**
 * Turn the server's series into pixels.
 *
 * The x-axis is *calendar* position across the whole window, not index-within-
 * the-series: a day with no weigh-in leaves a gap in the dots, while the rolling
 * line — which the server computes for every day that has a reading — connects
 * straight through it. Drawing by array index instead would silently compress a
 * fortnight of missed weigh-ins into a single step, which is exactly the thing
 * a 90-day chart exists to show.
 *
 * The window ends on the later of today and the last reading. Today is the
 * browser's, which can differ from the server's `MILO_TIMEZONE` day by one —
 * worth at most one pixel of x-offset here, and never worth a wrong reading,
 * because taking the max means a series that already runs to the server's today
 * pins the axis to *its* last point regardless of what this clock says.
 */
function buildChart(
  points: readonly WeightTrendPoint[],
  days: number,
  goalWeight: number | null,
): Chart | null {
  if (points.length === 0) return null

  const last = parseIso(points[points.length - 1].date)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const end = last.getTime() > today.getTime() ? last : today
  const start = new Date(end.getTime())
  start.setDate(start.getDate() - (days - 1))
  const span = Math.max(days - 1, 1)

  const x = (iso: string): number => {
    const offset = Math.min(Math.max(daysBetween(start, parseIso(iso)), 0), span)
    return round1(PAD_L + (offset / span) * (W - PAD_L - PAD_R))
  }

  // The domain is set from the readings, not from the rolling average: a mean of
  // those readings always lies inside their range, so both series fit either way,
  // and using the readings keeps the scale stable when the average is smooth.
  //
  // The goal is in BOTH bounds, not just the lower one. A weight-loss goal sits
  // under every reading and only the floor has to reach it; a weight-gain goal
  // sits over them, and a domain that stopped at the highest reading would put
  // the pink line and its label above the plot — clipped by the viewBox, or
  // drawn over the title bar. Two lines here, so the chart does not depend on
  // which direction the user is going.
  const values = points.map((p) => p.value)
  const bounds = goalWeight === null ? values : [goalWeight, ...values]
  const lo = Math.min(...bounds) - Y_PAD
  const hi = Math.max(...bounds) + Y_PAD
  const y = (value: number): number => round1(PAD_T + ((hi - value) / (hi - lo)) * (H - PAD_T - PAD_B))

  const gridlines: Gridline[] = []
  for (
    let line = Math.ceil(lo / GRID_STEP) * GRID_STEP;
    line <= hi && gridlines.length < MAX_GRIDLINES;
    line += GRID_STEP
  ) {
    const at = Math.round(y(line))
    gridlines.push({ y: at, labelY: at + 4, label: String(Math.round(line)) })
  }

  const dots = points.map((p) => ({ x: x(p.date), y: y(p.value) }))

  // A single reading is a `M x y` with nothing after it, and SVG draws nothing
  // for a subpath of zero length — the one day on the chart would be invisible
  // in the series that is supposed to be the headline. The repeated point gives
  // the round linecap something to cap, so it renders as a dot.
  const steps = points.map((p, i) => `${i ? 'L' : 'M'}${x(p.date)} ${y(p.rolling_7d)}`)
  const rollingPath = steps.length === 1 ? `${steps[0]} L${x(points[0].date)} ${y(points[0].rolling_7d)}` : steps.join(' ')

  const goalY = goalWeight === null ? null : Math.round(y(goalWeight))

  return {
    gridlines,
    dots,
    rollingPath,
    goal: goalY === null ? null : { y: goalY, labelY: goalY - 8, label: lb(goalWeight) },
  }
}

/**
 * A 120×36 sparkline, autoscaled to its own metric — waist moves two inches
 * where arms move a third of one, and a shared scale would flatten one of them
 * into a straight line. There is no axis, so there is nothing to mislead.
 */
function sparkline(values: readonly number[]): string | null {
  if (values.length === 0) return null
  // One reading has no shape. Draw the flat line rather than a dot in a corner:
  // it says "measured once, no movement yet" instead of looking like a glitch.
  if (values.length === 1) return 'M4 18 L116 18'

  const hi = Math.max(...values)
  const lo = Math.min(...values)
  const range = hi - lo || 1
  return values
    .map((value, i) => {
      const x = (4 + (i / (values.length - 1)) * 112).toFixed(1)
      const y = (4 + ((hi - value) / range) * 28).toFixed(1)
      return `${i ? 'L' : 'M'}${x} ${y}`
    })
    .join(' ')
}

/* ── Goals ───────────────────────────────────────────────────────────────── */

/** One numeric goal out of the flat string map, or null if unset/unreadable. */
function goalNumber(goals: Record<string, string>, key: string): number | null {
  const raw = goals[key]
  if (raw === undefined) return null
  // Settings writes these, and its fields show `−1.2` with a typographic minus.
  // Accept either sign character rather than silently dropping the goal.
  const value = Number.parseFloat(raw.trim().replace('−', '-'))
  return Number.isFinite(value) ? value : null
}

/* ── Screen ──────────────────────────────────────────────────────────────── */

export function Trends() {
  /* The app-shell FatSecret sync (issue #88). FatSecret writes weight rows too,
     so a bumped epoch re-runs the reads. No staleness pill here: LOG and MACROS
     carry the label for the day the sync is about, and a third copy of it on a
     90-day chart would overstate what one missed pull changes. */
  const { epoch } = useShellSync()

  const [core, setCore] = useState<Load<Core>>({ status: 'loading' })
  const [tape, setTape] = useState<Load<TapeSeries>>({ status: 'loading' })

  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller

    void (async () => {
      try {
        const [trends, goals] = await Promise.all([
          getTrends({ days: TREND_DAYS }, signal),
          // Soft: the goal decorates the screen (a pink line, two chips) but is
          // not the screen. A user who has never set one still gets the chart.
          // A failure that would also have failed /trends — no token, say — is
          // reported by /trends, so nothing is swallowed.
          getGoals(signal).catch(() => ({ goals: {} as Record<string, string> })),
        ])
        setCore({
          status: 'ready',
          data: {
            trends,
            goalWeight: goalNumber(goals.goals, GOAL_WEIGHT_KEY),
            weeklyRate: goalNumber(goals.goals, GOAL_RATE_KEY),
          },
        })
      } catch (error) {
        if (isAbort(error)) return
        setCore({ status: 'error', message: errorMessage(error) })
      }
    })()

    void (async () => {
      try {
        // /history answers for one metric at a time, so five metrics is five
        // requests. They are only the sparkline shapes; the numbers beside them
        // come from /trends.
        const series = await Promise.all(
          TAPE_METRICS.map((m) => getHistory({ metric: m.metric, days: TREND_DAYS }, signal)),
        )
        const data: TapeSeries = {}
        TAPE_METRICS.forEach((m, i) => {
          data[m.metric] = series[i].points.map((p: HistoryPoint) => p.value)
        })
        setTape({ status: 'ready', data })
      } catch (error) {
        if (isAbort(error)) return
        setTape({ status: 'error', message: errorMessage(error) })
      }
    })()

    return () => controller.abort()
    // `epoch` is the shell sync's refresh signal — see `useShellSync` above.
  }, [epoch])

  const ready = core.status === 'ready' ? core.data : null
  const weight = ready?.trends.weight ?? null
  const chart = ready ? buildChart(weight?.points ?? [], ready.trends.days, ready.goalWeight) : null

  const rows = ready
    ? TAPE_METRICS.flatMap((m) => {
        const summary = ready.trends.measurements[m.metric]
        return summary ? [{ ...m, summary }] : []
      })
    : []

  /* Tiles read `—` while loading and while broken alike. The distinction is
     made once, in the strip above them, rather than four times in the row. */
  const tile = (value: string): string => (ready ? value : '—')

  return (
    <>
      {core.status === 'error' ? (
        <Alert tone="danger" role="alert" title="Can’t read your trends.">
          {core.message}
        </Alert>
      ) : null}

      <div className="milo-metrics">
        <Metric
          color="yellow"
          label="ROLLING 7D"
          value={tile(lb(weight?.current_rolling_7d ?? null))}
          delta={ready && ready.goalWeight !== null ? `GOAL ${lb(ready.goalWeight)}` : undefined}
          deltaTone="down"
        />
        <Metric
          color="mint"
          label="Δ 7 DAYS"
          value={tile(delta(weight?.delta_7d ?? null))}
          delta="LB"
          deltaTone="down"
        />
        <Metric
          color="white"
          label="Δ 30 DAYS"
          value={tile(delta(weight?.delta_30d ?? null))}
          delta="LB"
          deltaTone="down"
        />
        <Metric
          color="pink"
          label="RATE / WK"
          // The rate the last week actually ran at is the 7-day delta — the same
          // number as the tile beside it, against a different yardstick: that one
          // is measured against the plan's weekly target, this one against zero.
          value={tile(delta(weight?.delta_7d ?? null))}
          delta={`PLAN ${ready && ready.weeklyRate !== null ? delta(ready.weeklyRate) : '—'}`}
          deltaTone="down"
        />
      </div>

      <Window title="WEIGHT.90D" shadow="m">
        {core.status === 'loading' ? (
          <div className="milo-note milo-note--sm">LOADING…</div>
        ) : chart === null ? (
          /* No weigh-ins at all — a brand-new account, or a failed read.

             The chart is deliberately not drawn at all rather than drawn empty.
             An axis with no domain has no gridlines to label and no line to
             hang the goal off; every coordinate in it would be a NaN, and SVG
             renders a NaN path as nothing while leaving the frame behind — a
             blank plot that looks like a bug rather than like an invitation. */
          <div className="milo-note milo-note--sm">
            {core.status === 'error' ? 'CHART UNAVAILABLE' : 'NO WEIGH-INS YET ✦ LOG ONE ON THE LOG TAB'}
          </div>
        ) : (
          <div className="milo-chart">
            <svg
              className="milo-chart-svg"
              viewBox={`0 0 ${W} ${H}`}
              role="img"
              aria-label={`Weight over the last ${ready?.trends.days ?? TREND_DAYS} days. Rolling 7-day average now ${lb(weight?.current_rolling_7d ?? null)} pounds.`}
            >
              {chart.gridlines.map((line) => (
                <g key={line.label}>
                  <line
                    x1={PAD_L}
                    x2={W - PAD_R}
                    y1={line.y}
                    y2={line.y}
                    stroke="#111111"
                    strokeWidth={1}
                    opacity={0.18}
                  />
                  <text
                    x={4}
                    y={line.labelY}
                    fontFamily="'Geist Mono', monospace"
                    fontSize={11}
                    fontWeight={700}
                    fill="#656565"
                  >
                    {line.label}
                  </text>
                </g>
              ))}

              {chart.goal ? (
                <>
                  <line
                    x1={PAD_L}
                    x2={W - PAD_R}
                    y1={chart.goal.y}
                    y2={chart.goal.y}
                    stroke="#FF4DCB"
                    strokeWidth={2.5}
                    strokeDasharray="7 6"
                  />
                  <text
                    x={W - PAD_R}
                    y={chart.goal.labelY}
                    textAnchor="end"
                    fontFamily="'Geist Mono', monospace"
                    fontSize={11}
                    fontWeight={800}
                    fill="#FF4DCB"
                  >
                    GOAL {chart.goal.label}
                  </text>
                </>
              ) : null}

              {/* Dots first, line over them: the rolling average is the headline
                  series and must never be broken up by the readings it averages. */}
              {chart.dots.map((dot, i) => (
                <circle key={i} cx={dot.x} cy={dot.y} r={2.6} fill="#2F5BFF" opacity={0.55} />
              ))}

              <path
                d={chart.rollingPath}
                fill="none"
                stroke="#111111"
                strokeWidth={3.5}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </svg>

            <div className="milo-legend">
              <span>━ ROLLING 7-DAY</span>
              <span className="milo-legend-dot">● DAILY WEIGH-IN</span>
              <span className="milo-legend-goal">╌ GOAL</span>
            </div>
          </div>
        )}
      </Window>

      <Window title="TAPE.90D" shadow="m">
        {core.status === 'loading' ? (
          <div className="milo-note milo-note--sm">LOADING…</div>
        ) : rows.length === 0 ? (
          <div className="milo-note milo-note--sm">
            {core.status === 'error' ? 'TAPE UNAVAILABLE' : 'NO TAPE READINGS YET ✦ MEASURE ONCE A WEEK'}
          </div>
        ) : (
          <div className="milo-rows">
            {rows.map((row) => {
              const path = tape.status === 'ready' ? sparkline(tape.data[row.metric] ?? []) : null
              return (
                <div className="milo-row milo-tape-row" key={row.metric}>
                  <span className="milo-tape-label">{row.label}</span>
                  {/* aria-hidden: the sparkline repeats the value and the two
                      deltas that follow it, and has nothing of its own to say. */}
                  <svg className="milo-spark" viewBox="0 0 120 36" aria-hidden="true">
                    {path ? (
                      <path
                        d={path}
                        fill="none"
                        stroke="#111111"
                        strokeWidth={2.5}
                        strokeLinejoin="round"
                      />
                    ) : null}
                  </svg>
                  <span className="milo-tape-value">
                    {row.summary.latest.toFixed(1)}
                    <span className="milo-tape-unit"> IN</span>
                  </span>
                  <span className="milo-tape-chip milo-tape-chip--start">
                    {delta(row.summary.delta_since_start)} START
                  </span>
                  <span className="milo-tape-chip milo-tape-chip--30d">
                    {delta(row.summary.delta_30d)} 30D
                  </span>
                </div>
              )
            })}
            <div className="milo-rows-foot">DELTAS IN INCHES ✦ MEASURED ROUGHLY WEEKLY</div>
          </div>
        )}
      </Window>
    </>
  )
}

export default Trends
